import { getErrorNotification, getTrialNotification } from './AddinErrorNotificationFactory';
import * as ContextActivationManagerFactory from './ContextActivationManagerFactory';
import { registerEndpoints, unregisterEndpoints } from './EndpointRegistrator';
import { ManifestCacheProvider } from './ManifestCacheProvider';
import { createDictionaryFromOsfControl, OsfControl } from './OsfControl';
import { TelemetryEvent } from '@ms/oteljs';
import { getAriaEvent } from '@ms/oteljs-aria/dist/src/GetAriaEvent';
import { logAddinUsage } from 'owa-addins-analytics';
import { addOTelColumnData } from 'owa-addins-analytics';
import { deleteAllApiEventHandlers } from 'owa-addins-events';
import { isFeatureEnabled } from 'owa-feature-flags';
import { extensibilityState, getEntryPointForControl } from 'owa-addins-store';
import { lazyGetOTelAddinsLogger } from 'owa-analytics/lib/LazyAnalytics';
import ExtensionInstallScope from 'owa-service/lib/contract/ExtensionInstallScope';
import LicenseType from 'owa-service/lib/contract/LicenseType';
import { getUserCulture } from 'owa-session-store';

const UserInstallScope: ExtensionInstallScope = 'User';
const OrgInstallScope: ExtensionInstallScope = 'Organization';

export type NotifyHostActions = {
    [action: number]: (controlId: string, data: {}) => Promise<void>;
};
export type OnInsertOsfControlDelegate = () => void;
export type DisplayNotificationDelegate = (args: OSF.Notification) => void;

export class OsfControlAdapter {
    private hostItemIndex: string;

    private contextActivationManager: OSF.ContextActivationManager;
    private activeOsfControl: OsfControl;
    private extensionContainer: HTMLDivElement;

    private notifyHostActions: NotifyHostActions;
    private onInsertOsfControlAction: OnInsertOsfControlDelegate;
    private onDisplayNotificationAction: DisplayNotificationDelegate;
    private manifestCacheProvider: ManifestCacheProvider;

    constructor(
        hostItemIndex: string,
        manifestCacheProvider: ManifestCacheProvider,
        notifyHostActions?: NotifyHostActions,
        onInsertOsfControl?: OnInsertOsfControlDelegate,
        onDisplayNotification?: DisplayNotificationDelegate
    ) {
        this.hostItemIndex = hostItemIndex;
        this.notifyHost = this.notifyHost.bind(this);
        this.contextActivationManager = ContextActivationManagerFactory.createContextActivationManager(
            getUserCulture(),
            this.notifyHost
        );
        this.notifyHostActions = notifyHostActions || {};
        this.onInsertOsfControlAction = onInsertOsfControl;
        this.onDisplayNotificationAction = onDisplayNotification;
        this.manifestCacheProvider = manifestCacheProvider;
        const externalNotifyError = this.notifyHostActions[OSF.AgaveHostAction.NotifyHostError];
        this.notifyHostActions[OSF.AgaveHostAction.NotifyHostError] = async (
            controlId: string,
            data: { errorCode: number }
        ) => {
            if (externalNotifyError) {
                externalNotifyError(controlId, data);
            }
            this.notifyHostError(controlId, data);
        };
        this.notifyHostActions[
            OSF.AgaveHostAction.SendTelemetryEvent
        ] = this.sendTelemetryEvent.bind(this);
    }

    public insertOsfControl(osfControl: OsfControl, extensionContainer: HTMLDivElement): void {
        registerEndpoints(this.hostItemIndex, this.contextActivationManager._serviceEndPoint);
        this.activeOsfControl = osfControl;
        this.extensionContainer = extensionContainer;
        this.manifestCacheProvider.cache(osfControl, getUserCulture());
        this.contextActivationManager.insertOsfControl(
            createDictionaryFromOsfControl(
                this.hostItemIndex,
                this.activeOsfControl,
                extensionContainer
            )
        );
    }

    public purgeOsfControl(): void {
        if (!!this.activeOsfControl && !!this.extensionContainer) {
            const { controlId } = this.activeOsfControl;
            unregisterEndpoints(this.hostItemIndex, this.contextActivationManager._serviceEndPoint);
            deleteAllApiEventHandlers(controlId);
            this.contextActivationManager.purgeOsfControl(controlId, false);
            this.activeOsfControl = null;
            this.extensionContainer = null;
        }
    }

    public onInsertOsfControl(): void {
        const {
            addinCommand: { extension },
        } = this.activeOsfControl;

        if (
            (extension.OriginString == UserInstallScope ||
                extension.OriginString == OrgInstallScope) &&
            extensibilityState.Context &&
            extensibilityState.Context.IsInOrgMarketplaceRole
        ) {
            this.tryDisplayNotification(extension.AppStatus, extension.LicenseType);
        }

        if (!!this.onInsertOsfControlAction) {
            this.onInsertOsfControlAction();
        }
    }

    public notifyAgave(action: OSF.AgaveHostAction) {
        const { controlId } = this.activeOsfControl;
        this.contextActivationManager.notifyAgave(controlId, action);
    }

    public updateHostItemIndex(hostItemIndex: string) {
        unregisterEndpoints(this.hostItemIndex, this.contextActivationManager._serviceEndPoint);

        this.hostItemIndex = hostItemIndex;
        registerEndpoints(hostItemIndex, this.contextActivationManager._serviceEndPoint);
    }

    public dispose(): void {
        this.contextActivationManager.dispose();
        this.contextActivationManager = null;
    }

    public async notifyHost(controlId: string, action: OSF.AgaveHostAction, data: {}) {
        const hostActions = this.notifyHostActions;
        if (hostActions) {
            const handler = hostActions[action];
            if (handler) {
                await handler(controlId, data);
            }
        }
    }

    private notifyHostError(controlId: string, data: { errorCode?: number }) {
        const { addinCommand } = this.activeOsfControl;
        logAddinUsage(
            false /* success */,
            addinCommand,
            this.hostItemIndex,
            getEntryPointForControl(controlId),
            data.errorCode
        );
    }

    private async sendTelemetryEvent(controlId: string, event: TelemetryEvent): Promise<void> {
        if (isFeatureEnabled('addin-oteljs')) {
            const logger = await lazyGetOTelAddinsLogger(event.telemetryProperties.ariaTenantToken);
            const ariaEvent = getAriaEvent(event, [], Date.now());
            const { addinCommand, controlId } = this.activeOsfControl;
            addOTelColumnData(ariaEvent, addinCommand, this.hostItemIndex, controlId);
            logger.logEvent(ariaEvent);
        }
    }

    private displayNotification(args: OSF.Notification) {
        this.contextActivationManager.displayNotification(args);

        if (!!this.onDisplayNotificationAction) {
            this.onDisplayNotificationAction(args);
        }
    }

    private tryDisplayNotification(status: string, licenseType: LicenseType) {
        const { addinCommand, controlId } = this.activeOsfControl;
        if (!!status) {
            const notification = getErrorNotification(controlId, addinCommand, status);
            if (notification) {
                this.displayNotification(notification);
            }
        } else if (licenseType == LicenseType.Trial) {
            this.displayNotification(getTrialNotification(addinCommand, controlId));
        }
    }
}
